import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { isAxiosError } from 'axios';

import { useStudioStore } from '~entities/studios/model/studio-store';

import { baseAPI } from '~shared/api/base-api';
import { KEYS } from '~shared/api/query-keys';

export interface UpdateEmailTemplateAPIRequest {
  subject: string;
  paragraph1: string;
  paragraph2: string;
}

export interface EmailTemplateResponse {
  _id: string;
  studioId: string;
  subject: string;
  paragraph1: string;
  paragraph1ImageUrl: string;
  paragraph1ImageName: string;
  paragraph2: string;
  paragraph2ImageUrl: string;
  paragraph2ImageName: string;
  createdAt: Date;
  updatedAt: Date;
  __v: number;
}

export const getEmailTemplateAPI = async (): Promise<EmailTemplateResponse | null> => {
  try {
    const resp = await baseAPI.get<EmailTemplateResponse>('/email-template');
    return resp.data;
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 404) {
      return null;
    }
    throw error;
  }
};

export const getEmailTemplateOptions = (studioId: string | null) => {
  return queryOptions({
    queryKey: [KEYS.EMAIL_TEMPLATE, studioId],
    queryFn: getEmailTemplateAPI,
    enabled: !!studioId,
    staleTime: 60 * 60 * 1000,
  });
};

export const useGetEmailTemplateQuery = () => {
  const studioId = useStudioStore((state) => state.currentStudioId);
  return useQuery(getEmailTemplateOptions(studioId));
};

export const updateEmailTemplateAPI = async (formData: FormData) => {
  const resp = await baseAPI.patch<EmailTemplateResponse>(
    '/email-template',
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  );
  return resp.data;
};

export const useUpdateEmailTemplateMutation = () => {
  const queryClient = useQueryClient();
  const studioId = useStudioStore((state) => state.currentStudioId);

  return useMutation({
    mutationFn: async (params: { template: FormData }) => {
      const response = await updateEmailTemplateAPI(params.template);
      return response;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [KEYS.EMAIL_TEMPLATE, studioId],
      });
    },
  });
};
